import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { ManualTrailStatus } from '../../../models/ManualTrailStatus.model'
import { ManualTrailStatusRelations } from '../../../models/ManualTrailStatusRelations.model'
import { TrailStatuses } from '../../../models/TrailStatuses.model'

import UserService from '../../../services/UserService/UserService'
import { ManualTrailStatusService } from '../../../services/ManualTrailStatusService/ManualTrailStatusService'

import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import ManualTrailStatusForm from '../shared/ManualTrailStatusForm.component'
import { useQuery, useQueryClient } from 'react-query'
import { AppStateContext } from '../../../providers/AppStateContext'
import { startCase } from 'lodash'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { ManualTrailStatusSectionIds, manualTrailStatusSections } from '../shared/ManualTrailStatusSections'

// theme colors for the status chip, keyed by TrailStatuses value
const statusChipColors: Record<number, 'default' | 'error' | 'warning' | 'success'> = {
  [TrailStatuses.Closed]: 'default',
  [TrailStatuses.HighImpact]: 'error',
  [TrailStatuses.MediumImpact]: 'warning',
  [TrailStatuses.LowImpact]: 'success',
}

const ManualTrailStatusDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editManualTrailStatus])
  const hasTrailPermissions = UserService.hasPermissions(user, [AppPermissions.viewTrail])
  const hasTrailSystemPermissions = UserService.hasPermissions(user, [AppPermissions.viewTrailSystem])

  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const manualTrailStatusService = useMemo(() => new ManualTrailStatusService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Manual Status Detail`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading, data: manualTrailStatus } = useQuery(
    [
      'ManualTrailStatusService.get',
      {
        id: Number(id),
        relations: [ManualTrailStatusRelations.system, ManualTrailStatusRelations.trail],
      },
      AppStateContext.getRegion(),
    ],
    () =>
      manualTrailStatusService.get({
        id: Number(id),
        relations: [ManualTrailStatusRelations.system, ManualTrailStatusRelations.trail],
      }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load manual status.')
        console.error(err)
      },
    },
  )

  const handleEdit = () => {
    closeEditor()
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (manualTrailStatus?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      manualTrailStatusService
        .delete(manualTrailStatus.id)
        .then(() => {
          SnackbarContext.show(
            `Manual Status deleted successfully. You will now be redirected.`,
            'success',
            APP_UPDATE_NOTICE,
          )

          // after a delay of 1 second, redirect to the manualTrailStatus list page
          setTimeout(() => {
            navigate(`/manual-trail-statuses`)
            queryClient.removeQueries([
              'ManualTrailStatusService.get',
              {
                id: Number(id),
                relations: [ManualTrailStatusRelations.system, ManualTrailStatusRelations.trail],
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Manual Status failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const validStatus = Object.entries(TrailStatuses).find((trailStatus) => trailStatus[1] === manualTrailStatus?.status)
  const statusLabel = validStatus ? startCase(validStatus[0]) : 'Unknown'
  const statusChip = manualTrailStatus ? (
    <Chip
      size="small"
      variant="outlined"
      label={statusLabel}
      color={(validStatus && statusChipColors[manualTrailStatus.status]) || 'default'}
    />
  ) : null
  // a trail-level status has no system of its own, so fall back to the trail's system when it was returned
  const assignedSystem = manualTrailStatus?.system ?? manualTrailStatus?.trail?.system
  const sectionById = (sectionId: ManualTrailStatusSectionIds) =>
    manualTrailStatusSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: ManualTrailStatusSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined

  return (
    <>
      {manualTrailStatus && (manualTrailStatus.system || manualTrailStatus.trail) && (
        <FormDialog<ManualTrailStatus>
          open={isEditing}
          entity={manualTrailStatus}
          entityLabel="Manual Trail Status"
          onClose={closeEditor}
        >
          <ManualTrailStatusForm
            manualTrailStatus={manualTrailStatus}
            trail={manualTrailStatus.trail}
            trailSystem={manualTrailStatus.system}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this manual status?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && manualTrailStatus && !manualTrailStatus.system && !manualTrailStatus.trail && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">
              Failed to locate the assigned trail system or trail for the requested manual status.
            </Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && manualTrailStatus && (manualTrailStatus.system || manualTrailStatus.trail) && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={() => {
                      const breadcrumbs: Array<{ label: string; href?: string }> = [
                        {
                          label: 'Manual Statuses',
                          href: '/manual-trail-statuses',
                        },
                      ]
                      if (hasTrailSystemPermissions && manualTrailStatus.system) {
                        breadcrumbs.push({
                          label: manualTrailStatus.system.name || 'Trail System',
                          href: `/trail-systems/${manualTrailStatus.system.id}`,
                        })
                      }
                      if (hasTrailPermissions && manualTrailStatus.trail) {
                        breadcrumbs.push({
                          label: manualTrailStatus.trail.name || 'Trail',
                          href: `/trails/${manualTrailStatus.trail.id}`,
                        })
                      }
                      breadcrumbs.push({ label: `Manual Status ID: ${manualTrailStatus.id}` })

                      return breadcrumbs
                    }}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <DetailHeader
                  title={manualTrailStatus.reason || `Manual Status ${manualTrailStatus.id}`}
                  badges={statusChip}
                  subtitle={`${manualTrailStatus.region} · ID ${manualTrailStatus.id}`}
                  meta={[
                    `Updated ${formatDate(manualTrailStatus.updatedAt)}`,
                    `Created ${formatDate(manualTrailStatus.createdAt)}`,
                  ]}
                />

                <Grid container spacing={2}>
                  {/* --- General ------------------------------------------------- */}
                  <Grid item zero={12} md={12}>
                    <DetailSection
                      section={sectionById(ManualTrailStatusSectionIds.general)}
                      onEdit={editSectionHandler(ManualTrailStatusSectionIds.general)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Status">{statusChip}</DetailField>
                        <DetailField label="Reason">{manualTrailStatus.reason || '-'}</DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Applies To ---------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(ManualTrailStatusSectionIds.assignment)}
                      onEdit={editSectionHandler(ManualTrailStatusSectionIds.assignment)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Trail System">
                          {assignedSystem?.id ? (
                            hasTrailSystemPermissions ? (
                              <Link href={`/trail-systems/${assignedSystem.id}`}>{assignedSystem.name}</Link>
                            ) : (
                              assignedSystem.name
                            )
                          ) : (
                            '-'
                          )}
                        </DetailField>
                        <DetailField label="Trail">
                          {manualTrailStatus.trail?.id ? (
                            hasTrailPermissions ? (
                              <Link href={`/trails/${manualTrailStatus.trail.id}`}>{manualTrailStatus.trail.name}</Link>
                            ) : (
                              manualTrailStatus.trail.name
                            )
                          ) : (
                            'All trails in the system'
                          )}
                        </DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Schedule ------------------------------------------------ */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(ManualTrailStatusSectionIds.schedule)}
                      onEdit={editSectionHandler(ManualTrailStatusSectionIds.schedule)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Start">
                          {manualTrailStatus.start ? formatDate(manualTrailStatus.start) : 'Immediately'}
                        </DetailField>
                        <DetailField label="End">
                          {manualTrailStatus.end ? formatDate(manualTrailStatus.end) : 'Never'}
                        </DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default ManualTrailStatusDetail
